import { AspectRatio, Box, Container, Flex, Image, Show } from '@telescope/cassini-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
// Styles
import { stylesheet } from './BracketGrid.styles';
// Helpers
import { useDisclosure, useWidget, VoteAPIResponseCode } from '@telescope/cassini-hooks';
import { ErrorPage, ThankYou, useAuth, useModals, VoteButton } from 'features';
import { useMediaQuery } from 'react-responsive';
import { useIntersection, useStateList } from 'react-use';
import { ErrorType, VoteOption } from 'types';
import { breakpoints } from 'utils/styles';
import { useBracketGrid } from '../providers/BracketGridProvider';

import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils';
import { useObserveSize } from '../hooks/useObserveSize';
import { BackToTopButton } from './BackToTopButton';
import { BracketGridVoteOption } from './BracketGridVoteOption';
import { VoteModal } from './VoteModal';
import { VoteOptionDetail } from './VoteOptionDetail';
import { Open } from 'generated-types/widget';

export const BracketGrid = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useWidget({ select: (data: Open) => data.widget.globalViews.bracketViewGrid });
  const { content } = data!;
  const voteOptionRef = useRef<HTMLDivElement | null>(null);
  const voteOptionSize = useObserveSize(voteOptionRef.current?.firstElementChild as HTMLElement);
  const {
    displayedRound,
    activeRound,
    totalRounds,
    voteOptions,
    winner,
    bracketType,
    pendingMatchupVote,
    totalContestants,
    isVoteOptionActive,
    isVoteOptionSelected,
    isVoteOptionVoteable,
    toggleVote,
    submitSingleVote,
    setPendingMatchupVote,
  } = useBracketGrid();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isDesktop = useMediaQuery({ minWidth: breakpoints.desktop });
  const style = stylesheet({
    columns: totalRounds,
    currentRoundDisplay: isDesktop ? 1 : displayedRound,
    voteOptionHeight: voteOptionSize.height,
    bracketSize: totalContestants,
  });
  const activeOptions = voteOptions.filter((option) => option.roundId === activeRound);
  const { prev, next, state: activeOption, setStateAt: setActiveOptionAt } = useStateList(activeOptions);
  const { openModal, closeAll } = useModals();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersection(ref, { rootMargin: '0px 0px -100% 0px' });
  const { data: snapshotId } = useWidget({ select: (data: Open) => data.sid });

  // Close all modals when snapshot changes
  useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotId]);

  const handleViewDetail = (option: VoteOption) => {
    const isActive = option.roundId === activeRound;

    if (isActive) {
      const index = activeOptions.findIndex((el) => option.referenceId === el.referenceId);
      setActiveOptionAt(index);
      onOpen();
    } else {
      openModal({
        children: <VoteOptionDetail voteOption={option} />,
        bodyProps: {
          p: [7, 0],
        },
      });
    }
  };

  const handleGridVoteButtonClick = (voteOption: VoteOption) => {
    if (bracketType === 'BY_MATCHUP') {
      handleViewDetail(voteOption);
      return;
    }

    toggleVote(voteOption);
  };

  const handleModalVoteButtonClick = (voteOption: VoteOption) => {
    if (bracketType === 'BY_MATCHUP') {
      submitVote(voteOption);
      return;
    }

    toggleVote(voteOption);
  };

  const submitVote = useCallback(
    async (voteOption: VoteOption) => {
      if (!user) {
        setPendingMatchupVote(voteOption);
        onClose();
        return navigate(ROUTES.LOGIN, { state: { backgroundLocation: ROUTES.HOME } });
      }

      let error = null;
      setIsSubmitting(true);

      try {
        const res = await submitSingleVote(voteOption);

        setIsSubmitting(false);

        if (res.response_code === VoteAPIResponseCode.VALID) {
          onClose(); // close vote modal

          return openModal({
            children: <ThankYou onViewVotes={closeAll} isVoteAgainAllowed={false} />,
            overlayProps: {
              bg: 'rgba(0, 0, 0, 1)',
            },
          });
        }

        const errors = {
          [VoteAPIResponseCode.WINDOW]: ErrorType.WINDOW,
          [VoteAPIResponseCode.OVERLIMIT]: ErrorType.OVERLIMIT,
          [VoteAPIResponseCode.GENERAL_INVALID]: ErrorType.GENERIC,
        };

        // @ts-expect-error: res.response_code might not be a property of errors
        // In this case use the known property VoteAPIResponseCode.GENERAL_INVALID
        error = errors[res.response_code] || errors[VoteAPIResponseCode.GENERAL_INVALID];
      } catch (err) {
        setIsSubmitting(false);
        error = ErrorType.GENERIC;
      }
      onClose(); // close vote modal
      openModal({ children: <ErrorPage errorType={error} /> });
    },
    [closeAll, navigate, onClose, openModal, setPendingMatchupVote, submitSingleVote, user]
  );

  useEffect(() => {
    (async () => {
      if (user && pendingMatchupVote) {
        setPendingMatchupVote(null);
        await submitVote(pendingMatchupVote);
      }
    })();
  }, [pendingMatchupVote, setPendingMatchupVote, submitVote, user]);

  const renderVoteOption = (voteOption: VoteOption, isWinner: boolean = false, index: number) => {
    const { roundId, vote_group_id: voteGroupId, region, position } = voteOption;
    const roundDisplay = isDesktop ? roundId : roundId - displayedRound + 1;
    const isActive = isVoteOptionActive(voteOption);
    const isSelected = isActive && isVoteOptionSelected(voteOption);

    return (
      <BracketGridVoteOption
        index={index}
        onViewDetail={handleViewDetail}
        voteOption={voteOption}
        columns={totalRounds}
        roundDisplay={roundDisplay}
        isActive={isVoteOptionActive(voteOption)}
        isVisible={isWinner || roundDisplay > 0}
        key={`${voteGroupId}${region}${position}`}
        isWinner={isWinner}
        voteButton={
          isWinner ? undefined : (
            <VoteButton
              data-vote-option-name={voteOption.name}
              isDisabled={!isVoteOptionVoteable(voteOption)}
              isSelected={isSelected}
              onClick={() => handleGridVoteButtonClick(voteOption)}
              pos="absolute"
              display="block"
              width="100%"
              rounded="0"
              borderBottomRightRadius="10px"
              borderBottomLeftRadius="10px"
              p={2}>
              {content.voteButton}
            </VoteButton>
          )
        }
      />
    );
  };

  return (
    <>
      <Container
        className="BracketGrid BracketGrid__root"
        maxW={{ base: 'none', lg: 'container.xl' }}
        as="section"
        sx={style.container}
        ref={ref}
        aria-multiselectable="true"
        data-bracket-size={totalContestants}>
        <Box className="BracketGrid__left" sx={{ ...style.grid, ...style.region1 }} ref={voteOptionRef}>
          {voteOptions
            .filter((voteOption: VoteOption) => voteOption.region === 1)
            .map((voteOption: VoteOption, index) => renderVoteOption(voteOption, false, index))}
          <Show above="lg">
            <Flex className="BracketGrid__quadrant-title" sx={{ ...style.quadrantTitle, ...style.topLeft }}>
              {content.quadrantTitles?.topLeft}
            </Flex>
            <Flex className="BracketGrid__quadrant-title" sx={{ ...style.quadrantTitle, ...style.bottomLeft }}>
              {content.quadrantTitles?.bottomLeft}
            </Flex>
          </Show>
        </Box>

        <Box className="BracketGrid__center" sx={style.winnerContainer}>
          {winner ? (
            renderVoteOption(winner, true, 0)
          ) : (
            <AspectRatio ratio={16 / 9} width="100%" sx={{ '>img': { objectFit: 'contain' } }}>
              <Image
                className="BracketGrid__placeholderImage"
                alt=""
                role="presentation"
                src={content.winnerPlaceholderImage}
              />
            </AspectRatio>
          )}
        </Box>

        <Box className="BracketGrid__right" sx={{ ...style.grid, ...style.region2 }}>
          <Show above="lg">
            <Flex className="BracketGrid__quadrant-title" sx={{ ...style.quadrantTitle, ...style.topRight }}>
              {content.quadrantTitles?.topRight}
            </Flex>
            <Flex className="BracketGrid__quadrant-title" sx={{ ...style.quadrantTitle, ...style.bottomRight }}>
              {content.quadrantTitles?.bottomRight}
            </Flex>
          </Show>
          {voteOptions
            .filter((voteOption: VoteOption) => voteOption.region === 2)
            .map((voteOption: VoteOption, index) => renderVoteOption(voteOption, false, index))}
        </Box>
      </Container>

      <VoteModal
        isOpen={isOpen}
        onClose={onClose}
        onPrevious={prev}
        onNext={next}
        voteOption={activeOption}
        voteButton={
          bracketType === 'BY_MATCHUP' && !activeOption?.is_active ? undefined : (
            <VoteButton
              width="140px"
              height="65px"
              fontSize="lg"
              isDisabled={!isVoteOptionVoteable(activeOption)}
              isSelected={isVoteOptionSelected(activeOption)}
              isLoading={isSubmitting}
              onClick={() => handleModalVoteButtonClick(activeOption)}>
              {content.voteButton}
            </VoteButton>
          )
        }
      />

      <BackToTopButton isVisible={!isDesktop && !!entry?.isIntersecting} />
    </>
  );
};
